import {useEffect, useRef, useCallback, useContext} from "preact/hooks";
import {GoogleAuthProvider, signInWithCredential} from "firebase/auth";
import {auth} from "../includes/firebase.js";
import {NotificationContext} from "./NotificationContext.jsx";
import {useLocation} from "../hooks/useLocation.js";

export function GoogleOneTapClient({disabled = false}) {
    const oneTapInitialized = useRef(false);
    const clientId = import.meta.env.VITE_GOOGLE_TAP_CLIENT_ID;
    const location = useLocation();
    const {addNotification} = useContext(NotificationContext);

    const handleCredential = useCallback(async (credential) => {
        await signInWithCredential(
            auth,
            GoogleAuthProvider.credential(credential),
        ).then(() => {
            location.route('/account')
        }).catch((error) => {
            addNotification(error.message, "error");
        });
    }, [auth, location, addNotification]);

    useEffect(() => {
        if (!clientId || disabled) {
            return;
        }

        let retryTimerId = null;
        const scriptSrc = "https://accounts.google.com/gsi/client";
        const maxAttempts = 30;

        const initializeOneTap = () => {
            if (oneTapInitialized.current || !window.google?.accounts?.id) {
                return false;
            }

            oneTapInitialized.current = true;
            window.google.accounts.id.initialize({
                client_id: clientId,
                auto_select: false,
                callback: async (response) => {
                    if (!response?.credential) {
                        return;
                    }
                    await handleCredential(response.credential);
                },
            });
            window.google.accounts.id.prompt();
            return true;
        };

        const initializeWithRetry = (attempt = 0) => {
            if (initializeOneTap() || attempt >= maxAttempts) {
                return;
            }
            retryTimerId = window.setTimeout(() => initializeWithRetry(attempt + 1), 100);
        };

        let gsiScript = document.querySelector(`script[src="${scriptSrc}"]`);
        if (gsiScript) {
            gsiScript.addEventListener("load", initializeWithRetry);
            initializeWithRetry();
        } else {
            gsiScript = document.createElement("script");
            gsiScript.src = scriptSrc;
            gsiScript.async = true;
            gsiScript.defer = true;
            gsiScript.addEventListener("load", initializeWithRetry);
            document.head.appendChild(gsiScript);
        }

        return () => {
            if (retryTimerId) {
                window.clearTimeout(retryTimerId);
            }
            gsiScript.removeEventListener("load", initializeWithRetry);
            if (window.google?.accounts?.id) {
                window.google.accounts.id.cancel();
            }
        };
    }, [clientId, disabled, handleCredential]);

    return null;
}
